import {
  Box,
  Container,
  Icon,
  Stack,
  Typography,
  useMediaQuery,
} from '@ace-clublabs/shared-component-library';
import { useEffect, useState } from 'react';
import LoadingContainer from '@/components/shared/LoadingContainer';
import { constants } from '@/constants';
import useContextAwarenessParams from '@/hooks/useContextAwarenessParams';
import { useFeatureFlag } from '@/hooks/useFeatureFlags';
import useLandingPageSetup from '@/hooks/useLandingPageSetup';
import useProgress from '@/hooks/useProgress';
import { Page } from '@/lib/tagging';
import useTagging from '@/lib/tagging/useTagging';
import { selectTrvEndLocationForTagging } from '@/lib/tagging/utils';
import useAppointmentStore from '@/store/AppointmentStore';
import useFeatureFlagsStore from '@/store/FeatureFlagsStore';
import useProgressStore from '@/store/ProgressStore';
import useSessionStore from '@/store/SessionStore';
import { FlowType } from '@/types/FlowType';
import DestinationCards from '../components/DestinationCards';
import RSSearchBox from '../components/RSSearchBox';
import RSSearchBoxMobile from '../components/RSSearchBoxMobile';
import { useRSSearchBox } from '../hooks/useRSSearchBox';

interface RelevanceSearchProps {
  flow: FlowType;
}

const RELEVANCE_PROGRESS_PERCENT = 15;
const RelevanceSearch: React.FC<RelevanceSearchProps> = ({ flow }) => {
  const [tagged, setTagged] = useState(false);

  const isEmailFlow = flow === FlowType.EMAIL;

  const trvEndLocation = useAppointmentStore(selectTrvEndLocationForTagging);

  const { sessionId, sessionSource, flowType } = useSessionStore();
  const tagging = useTagging();

  const { isResolving } = useContextAwarenessParams(flow);
  const { proceedWithDestination, destinationsQuery } =
    useLandingPageSetup(flow);
  useProgress(RELEVANCE_PROGRESS_PERCENT);

  const isDestinationsLoading =
    isResolving ||
    destinationsQuery.isPending ||
    destinationsQuery.error?.message === constants.TANSTACK_ABORTED_ERROR;

  const isMobileView = useMediaQuery((theme) => theme.breakpoints.down('md'));
  const setShowProgress = useProgressStore((state) => state.setShowProgress);
  const enablePopularFeaturedDestinations = useFeatureFlag(
    'enablePopularFeaturedDestinations',
  );
  const featureFlagsReady = useFeatureFlagsStore(
    (state) => state.featureFlagsReady,
  );

  const { searchBoxProps, isSearchPanelOpen } = useRSSearchBox({
    flow,
    proceedWithDestination,
  });

  useEffect(() => {
    if (!isMobileView) {
      setShowProgress(true);
      return;
    }

    setShowProgress(!isSearchPanelOpen);
  }, [isMobileView, isSearchPanelOpen, setShowProgress]);

  useEffect(() => {
    return () => setShowProgress(true);
  }, [setShowProgress]);

  useEffect(() => {
    if (!tagged && sessionSource && sessionId && featureFlagsReady) {
      const commPref = isEmailFlow ? 'email' : undefined;

      const experimentFlags = `trv_scheduler_destcards:${enablePopularFeaturedDestinations ? 'on' : 'off'}`;

      tagging.tag('view', sessionSource || 'new', {
        page: Page.destination,
        sessionid: sessionId,
        flow: flowType || '',
        trv_end_location: trvEndLocation,
        ...(commPref && { comm_pref: commPref }),
        ...(experimentFlags && { experiment_flags: experimentFlags }),
      });

      setTagged(true);
    }
  }, [
    sessionId,
    flowType,
    sessionSource,
    tagging.tag,
    tagged,
    trvEndLocation,
    isEmailFlow,
    enablePopularFeaturedDestinations,
    featureFlagsReady,
  ]);

  if (isDestinationsLoading) {
    const loadingMessage = isEmailFlow
      ? 'Your advisor app is loading...'
      : 'Your new adventure awaits...';
    return <LoadingContainer message={loadingMessage} showRadios={false} />;
  }

  return (
    <Stack
      direction='column'
      sx={{
        width: '100%',
        height: '100%',
        overflow: 'auto',
        textAlign: 'start',
        backgroundColor: '#F7F5F1',
      }}
      data-testid='relevance-search-page'
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: { xs: 341, md: 'auto' },
          minHeight: { md: 680 },
          ...(!enablePopularFeaturedDestinations && {
            flex: 1,
            height: 'auto',
          }),
          backgroundColor: '#222222',
          py: 4,
          borderRadius: enablePopularFeaturedDestinations
            ? { xs: '0 0 16px 16px', md: 0 }
            : 0,
        }}
        data-testid='relevance-search-hero-section'
      >
        <Box
          component='picture'
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            zIndex: 0,
            overflow: 'hidden',
            borderRadius: 'inherit',
          }}
          data-testid='relevance-search-hero-image'
        >
          <source
            media='(min-width: 1200px)'
            srcSet='https://assets.ace.aaa.com/is/image/aceaaa/GettyImages-2218004082:big-hero-1620x1080'
          />
          <source
            media='(min-width: 600px)'
            srcSet='https://assets.ace.aaa.com/is/image/aceaaa/GettyImages-2218004082:hero-full-width-1200x800'
          />
          <source
            media='(min-width: 400px)'
            srcSet='https://assets.ace.aaa.com/is/image/aceaaa/GettyImages-2218004082:cards-square-600x600'
          />
          <Box
            component='img'
            // @ts-expect-error
            src='https://assets.ace.aaa.com/is/image/aceaaa/GettyImages-2218004082:cards-square-mobile-360x360'
            alt=''
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        </Box>

        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundColor: 'rgba(10,10,10,0.4)',
            zIndex: 1,
            borderRadius: 'inherit',
          }}
          aria-hidden
          data-testid='relevance-search-hero-overlay'
        />
        <Container
          maxWidth='lg'
          sx={{
            zIndex: 2,
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          data-testid='relevance-search-hero-container'
        >
          <Stack
            direction='column'
            spacing={{ xs: 2, md: 4 }}
            sx={{
              width: '100%',
              px: { md: 6, xs: 2 },
              pt: { xs: 3, md: 6 },
              pb: { xs: 3, md: 5 },
              borderRadius: 2,
              alignItems: 'center',
              zIndex: 3,
            }}
            data-testid='relevance-search-hero-stack'
          >
            <Box data-testid='relevance-search-hero-logo'>
              <Icon
                iconName='ace-orbit-logo'
                sx={{
                  color: '#fff',
                  width: 85,
                  height: 52,
                  '& path': {
                    fill: 'white',
                  },
                }}
              />
            </Box>
            <Typography
              sx={{
                color: '#fff',
                textAlign: 'center',
                typography: { xs: 'h6', md: 'h4' },
                fontWeight: '700 !important',
              }}
              data-testid='relevance-search-hero-title'
            >
              {isEmailFlow
                ? 'Email a Travel Advisor'
                : 'Make an appointment with a travel advisor to plan and book travel'}
            </Typography>
            {isMobileView ? (
              <RSSearchBoxMobile {...searchBoxProps} />
            ) : (
              <RSSearchBox {...searchBoxProps} sx={{ width: '85%' }} />
            )}
          </Stack>
        </Container>
      </Box>
      {enablePopularFeaturedDestinations && proceedWithDestination && (
        <DestinationCards proceedWithDestination={proceedWithDestination} />
      )}
    </Stack>
  );
};

export default RelevanceSearch;
